

import React, { useCallback, useEffect, useState } from "react";
import {
  collection,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";

import { db } from "../firebase";
import "./ChangeLanguage.css";

const LANGUAGES = [
  { code: "en", nativeName: "English" },
  { code: "fr", nativeName: "Français" },
  { code: "sw", nativeName: "Kiswahili" },
  { code: "pt", nativeName: "Português" },
  { code: "es", nativeName: "Español" },
  { code: "pt-AO", nativeName: "Português (Angola)" },
  { code: "ar", nativeName: "العربية" },
  { code: "zh", nativeName: "中文" },
  { code: "hi", nativeName: "हिन्दी" },
  { code: "bn", nativeName: "বাংলা" },
  { code: "ru", nativeName: "Русский" },
  { code: "de", nativeName: "Deutsch" },
  { code: "it", nativeName: "Italiano" },
  { code: "nl", nativeName: "Nederlands" },
  { code: "tr", nativeName: "Türkçe" },
  { code: "ja", nativeName: "日本語" },
  { code: "ko", nativeName: "한국어" },
  { code: "vi", nativeName: "Tiếng Việt" },
  { code: "th", nativeName: "ไทย" },
  { code: "id", nativeName: "Bahasa Indonesia" },
  { code: "ms", nativeName: "Bahasa Melayu" },
  { code: "ur", nativeName: "اردو" },
  { code: "fa", nativeName: "فارسی" },
  { code: "ha", nativeName: "Hausa" },
  { code: "yo", nativeName: "Yorùbá" },
  { code: "zu", nativeName: "isiZulu" },
  { code: "am", nativeName: "አማርኛ" },
  { code: "so", nativeName: "Soomaali" },
  { code: "rw", nativeName: "Ikinyarwanda" },
  { code: "lg", nativeName: "Luganda" },
  { code: "af", nativeName: "Afrikaans" },
];

const STORAGE_KEYS = ["language", "appLanguage", "lang"];

const isValidLanguage = (code) =>
  typeof code === "string" &&
  LANGUAGES.some((item) => item.code === code);

const readStoredLanguage = () => {
  try {
    for (const key of STORAGE_KEYS) {
      const savedLanguage = localStorage.getItem(key);

      if (isValidLanguage(savedLanguage)) {
        return savedLanguage;
      }
    }
  } catch (error) {
    console.error("Failed to read language:", error);
  }

  return "en";
};

const saveLanguageLocally = (code) => {
  try {
    STORAGE_KEYS.forEach((key) => {
      localStorage.setItem(key, code);
    });
  } catch (error) {
    console.error("Failed to save language locally:", error);
  }
};

export default function LanguageSelector() {
  const [language, setLanguage] = useState(readStoredLanguage);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const notifyLanguageChange = useCallback((code) => {
    window.dispatchEvent(
      new CustomEvent("languageChanged", {
        detail: { language: code },
      })
    );
  }, []);

  // Sikiliza mabadiliko ya lugha kutoka sehemu nyingine za app.
  useEffect(() => {
    const handleLanguageChanged = (event) => {
      const code =
        event?.detail?.language ||
        (typeof event?.detail === "string"
          ? event.detail
          : "");

      if (isValidLanguage(code)) {
        setLanguage(code);
      }
    };

    const handleStorage = (event) => {
      if (!event.key || STORAGE_KEYS.includes(event.key)) {
        const code =
          event.newValue && isValidLanguage(event.newValue)
            ? event.newValue
            : readStoredLanguage();

        if (isValidLanguage(code)) {
          setLanguage(code);
        }
      }
    };

    window.addEventListener(
      "languageChanged",
      handleLanguageChanged
    );

    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(
        "languageChanged",
        handleLanguageChanged
      );

      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  // Pakia lugha iliyohifadhiwa kwenye akaunti ya Firestore.
  useEffect(() => {
    let cancelled = false;

    const loadAccountLanguage = async () => {
      try {
        const username = localStorage.getItem("username");

        if (!username) return;

        const usersQuery = query(
          collection(db, "users"),
          where("username", "==", username)
        );

        const snapshot = await getDocs(usersQuery);

        if (cancelled || snapshot.empty) return;

        const savedLanguage =
          snapshot.docs[0].data()?.language;

        if (!isValidLanguage(savedLanguage)) return;

        setLanguage(savedLanguage);
        saveLanguageLocally(savedLanguage);
        notifyLanguageChange(savedLanguage);
      } catch (error) {
        console.error(
          "Failed to load account language:",
          error
        );
      }
    };

    loadAccountLanguage();

    return () => {
      cancelled = true;
    };
  }, [notifyLanguageChange]);

  // Hifadhi lugha iliyochaguliwa.
  const saveLanguage = async (code) => {
    if (loading) return;

    if (!isValidLanguage(code)) {
      setMessage("Please select a valid language.");
      return;
    }

    // Badilisha lugha mara moja kwenye kifaa.
    setLanguage(code);
    setMessage("");
    saveLanguageLocally(code);
    notifyLanguageChange(code);

    let username = "";

    try {
      username = localStorage.getItem("username") || "";
    } catch (error) {
      console.error("Failed to read username:", error);
    }

    if (!username) {
      setMessage(
        "Language changed on this device. Log in to save it to your account."
      );
      return;
    }

    setLoading(true);

    try {
      const usersQuery = query(
        collection(db, "users"),
        where("username", "==", username)
      );

      const snapshot = await getDocs(usersQuery);

      if (snapshot.empty) {
        setMessage(
          "Language changed on this device, but user account was not found."
        );
        return;
      }

      await updateDoc(snapshot.docs[0].ref, {
        language: code,
      });

      setMessage("Language saved successfully.");
    } catch (error) {
      console.error("Failed to save language:", error);

      setMessage(
        "Language changed on this device, but could not be saved to your account."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="language-picker-inline">
      <label
        htmlFor="language-select"
        className="language-picker-label"
      >
        Language / Lugha
      </label>

      <div className="language-picker-select-wrapper">
        <select
          id="language-select"
          className="language-picker-select"
          value={language}
          onChange={(event) => saveLanguage(event.target.value)}
          disabled={loading}
          aria-label="Select language"
        >
          {LANGUAGES.map((item) => (
            <option key={item.code} value={item.code}>
              {item.nativeName}
            </option>
          ))}
        </select>

        {loading && (
          <span
            className="language-picker-loading"
            role="status"
          >
            Saving...
          </span>
        )}
      </div>

      {message && (
        <p
          className={`language-picker-message ${
            message.includes("successfully") ? "success" : ""
          }`}
          role="status"
        >
          {message}
        </p>
      )}
    </div>
  );
}
